/**
 * Reproduce MP3 en /public/alarms/ alineados con backend (prerecordings_siren).
 * Sonidos 1–5: `{n}.mp3`. Sonido 14: MP3 del stream vía API (distorsionado en backend).
 * Cualquier otro id (6–13, desconocido) o error/timeout de stream: no se reproduce nada.
 * Los navegadores suelen bloquear autoplay: use primeAlarmAudioOnUserGesture() al montar la app.
 */

import axios from 'axios';

/** WAV silencioso (1 muestra) para desbloquear autoplay sin depender de ningún archivo. */
const SILENT_UNLOCK_SRC =
  'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQIAAAAAAA==';

let current: HTMLAudioElement | null = null;
let audioPrimed = false;
let gestureListenersAttached = false;

/** Motivo de fallo al pedir el MP3 de stream (para mensajes en UI). */
export type StreamAudioPlayFailure = 'not_found' | 'unauthorized' | 'empty' | 'play_failed' | 'busy';

function alarmsBase(): string {
  const p = process.env.PUBLIC_URL || '';
  return `${p.replace(/\/$/, '')}/alarms`;
}

/** Nombre del MP3 pregrabado, o null si el id no está contemplado (no reproducir nada). */
export function resolveAlarmFilename(sound?: number | null): string | null {
  if (sound != null && sound >= 1 && sound <= 5) {
    return `${sound}.mp3`;
  }
  return null;
}

/**
 * Registra un “desbloqueo” en el primer clic/tecla: sin esto Chrome suele rechazar play() en alertas.
 * Llamar una vez al iniciar la app (p. ej. GlobalNotificationListener).
 */
export function primeAlarmAudioOnUserGesture(): void {
  if (gestureListenersAttached || typeof document === 'undefined') return;
  gestureListenersAttached = true;

  const tryPrime = () => {
    if (audioPrimed) return;
    const silent = new Audio(SILENT_UNLOCK_SRC);
    silent.volume = 0.001;
    void silent
      .play()
      .then(() => {
        silent.pause();
        audioPrimed = true;
      })
      .catch(() => {
        /* sigue bloqueado hasta más gestos; playAlarmSound lo intentará de nuevo */
      });
  };

  const onGesture = () => {
    tryPrime();
    document.removeEventListener('click', onGesture);
    document.removeEventListener('keydown', onGesture);
    document.removeEventListener('touchstart', onGesture, { capture: true });
  };

  document.addEventListener('click', onGesture);
  document.addEventListener('keydown', onGesture);
  document.addEventListener('touchstart', onGesture, { capture: true });
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

/** Alerta de stream (sonido 14) en reproducción; null si es genérico u otro. */
let currentStreamAlertId: number | null = null;

export function stopCurrentAlarmPlayback(): void {
  if (current) {
    current.pause();
    current.currentTime = 0;
    current = null;
  }
  currentStreamAlertId = null;
}

/** true si hay un MP3 de stream personalizado sonando ahora. */
export function isStreamAlertAudioPlaying(): boolean {
  return current != null && currentStreamAlertId != null && !current.paused;
}

export function getPlayingStreamAlertId(): number | null {
  return isStreamAlertAudioPlaying() ? currentStreamAlertId : null;
}

/**
 * MP3 del stream (sonido 14), ya convertido/distorsionado en backend. Requiere JWT.
 * Usa axios (no fetch) para que el interceptor renueve el access token en 401.
 * Una sola petición: el caller debe haber comprobado streamAudioReady (no reintentar 404 aquí).
 * Sin `force`, no interrumpe otro stream que ya esté sonando.
 */
export async function playButtonAlertStreamAudio(
  buttonAlertId: number,
  _token: string,
  options?: { force?: boolean }
): Promise<boolean> {
  const result = await playButtonAlertStreamAudioDetailed(buttonAlertId, options);
  return result.ok;
}

export async function playButtonAlertStreamAudioDetailed(
  buttonAlertId: number,
  options?: { force?: boolean }
): Promise<{ ok: true } | { ok: false; reason: StreamAudioPlayFailure }> {
  const force = options?.force === true;
  if (
    !force &&
    isStreamAlertAudioPlaying() &&
    currentStreamAlertId != null &&
    currentStreamAlertId !== buttonAlertId
  ) {
    return { ok: false, reason: 'busy' };
  }

  const url = `${API_URL}/button-alerts/${buttonAlertId}/audio`;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      // Token vía interceptor de axios (localStorage + refresh automático en 401).
      // No forzar validateStatus: los 401 deben rechazarse para que el interceptor renueve.
      const res = await axios.get<Blob>(url, { responseType: 'blob' });
      const blob = res.data;
      if (!(blob instanceof Blob) || blob.size < 256) {
        console.warn(
          '[AlarmSound] stream audio blob too small',
          buttonAlertId,
          blob instanceof Blob ? blob.size : typeof blob
        );
        return { ok: false, reason: 'empty' };
      }

      // Solo ahora detenemos lo anterior: el MP3 nuevo está listo para reproducir.
      stopCurrentAlarmPlayback();
      const objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(objectUrl);
      current = audio;
      currentStreamAlertId = buttonAlertId;
      audio.volume = 0.92;
      await audio.play();
      audio.onended = () => {
        URL.revokeObjectURL(objectUrl);
        if (current === audio) {
          current = null;
          currentStreamAlertId = null;
        }
      };
      return { ok: true };
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 404) {
        return { ok: false, reason: 'not_found' };
      }
      if (status === 401 || status === 403) {
        return { ok: false, reason: 'unauthorized' };
      }

      const msg = err instanceof Error ? err.message : String(err);
      const blocked =
        msg.includes('NotAllowed') ||
        msg.includes('not allowed') ||
        msg.includes('user didn');
      if (blocked && attempt < 2) {
        await new Promise((r) => window.setTimeout(r, 400));
        continue;
      }
      console.warn('[AlarmSound] stream audio play failed', err);
      return { ok: false, reason: 'play_failed' };
    }
  }
  return { ok: false, reason: 'play_failed' };
}

export function playAlarmSound(sound?: number | null): void {
  const name = resolveAlarmFilename(sound);
  if (!name) {
    // id no contemplado (6–13, 14 stream, desconocido) o error: no reproducir nada.
    return;
  }
  const src = `${alarmsBase()}/${name}`;

  try {
    stopCurrentAlarmPlayback();
    const audio = new Audio(src);
    current = audio;
    audio.volume = 0.92;
    void audio.play().catch((err: Error) => {
      console.warn(
        '[AlarmSound] play() rechazado. Haz clic una vez en la página para permitir audio, o revisa la consola Red si el MP3 da 404:',
        err?.message ?? err,
        '| src:',
        src
      );
    });
    audio.onended = () => {
      if (current === audio) current = null;
    };
  } catch (e) {
    console.warn('[AlarmSound]', e);
  }
}
